import React, { createContext, useContext, useMemo } from "react";
import {
  arraysHaveSameContents,
  hasAny,
  hasAll,
  hasPermission,
} from "../core/permissionUtils";
import { computeUiFlags, type UiPermissions } from "../core/uiFlags";

interface PermissionContextValue {
  permissions: string[];
  uiFlags: UiPermissions;
}

const PermissionContext = createContext<PermissionContextValue | null>(null);

interface PermissionProviderProps {
  permissions: string[];
  uiFlags?: UiPermissions;
  children: React.ReactNode;
}

export const PermissionProvider: React.FC<PermissionProviderProps> = ({
  permissions,
  uiFlags,
  children,
}) => {
  // Stable permissions reference to reduce rerenders
  const stablePermissionsRef = React.useRef<string[]>(permissions);

  if (!arraysHaveSameContents(stablePermissionsRef.current, permissions)) {
    stablePermissionsRef.current = permissions;
  }

  const value = useMemo<PermissionContextValue>(() => {
    const p = stablePermissionsRef.current;
    return {
      permissions: p,
      uiFlags: uiFlags ?? computeUiFlags(p),
    };
  }, [uiFlags, stablePermissionsRef.current]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

const usePermissionContext = (): PermissionContextValue => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error(
      "Permission context not found. Wrap your app with PermissionProvider."
    );
  }
  return ctx;
};

export const usePermissions = (): string[] =>
  usePermissionContext().permissions;

export const usePermission = (id: string): boolean => {
  const { permissions } = usePermissionContext();
  return useMemo(() => hasPermission(permissions, id), [permissions, id]);
};

export const useAnyPermission = (ids: string[]): boolean => {
  const { permissions } = usePermissionContext();
  return useMemo(() => hasAny(permissions, ids), [permissions, ids]);
};

export const useAllPermissions = (ids: string[]): boolean => {
  const { permissions } = usePermissionContext();
  return useMemo(() => hasAll(permissions, ids), [permissions, ids]);
};

export const useUiPermissions = (): UiPermissions =>
  usePermissionContext().uiFlags;
